import {FC, memo, MouseEvent, useCallback, useContext, useEffect, useState} from 'react';
import generatePDF from 'react-to-pdf';
import {toast} from 'react-toastify';
import {useRecoilState, useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';

import AdfScannerIcon from '@mui/icons-material/AdfScanner';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import LoadingButton from '@mui/lab/LoadingButton';
import {Button, Stack, Tooltip} from '@mui/material';
import {
  baseWorkData,
  contentTreeFilesQueryTrigger,
  currentReasonDelay,
  currentServiceRequestState,
  docsToReplaceState,
  editableWorkData,
  serviceRequestActiveTabState,
  serviceRequestsQueryTrigger,
} from '../../../store/service-requests/atoms';
import {DelayDocTemplate} from '../../../ui/doc-templates/DelayDocTemplate';
import {ConfirmAction} from '../../modals/ConfirmAction';
import {ModalContainer} from '../../modals/ModalContainer';
import {ServiceRequestRejectModal} from './ServiceRequestRejectModal';

import {EventBusContext} from '../../../providers/EventBus';

import {ServiceRequestService} from '../../../api/services/serviceRequest.service';

import {authState, IUserInfo} from '../../../store/auth/atoms';

import {getModelNamePrefix, populateBaseForm} from './WorkData/utils';
import {
  getTargetElement,
  IDelayDocVars,
  normalizeServiceRequestForADPUDoc,
  normalizeServiceRequestForDelayDoc,
} from './utils';

import {reasonRejectionsDictQuery} from '../../../store/reasonRejections/selectors';

import {DocumentService} from '../../../api/services/document.service';
import {filterDivider} from '../../../shared/helpers/filterDivider';
import {resolveAndDownloadBlob} from '../../../shared/helpers/resolveAndDownloadBlob';
import {IServiceRequest, StatusCodeEnum} from '../../../shared/types/serviceRequest.types';
import {InputTypeEnum, WorkContentType} from '../../../shared/types/workContent.types';
import {ADPUDocTemplate} from '../../../ui/doc-templates/ADPUDocTemplate';
import {ServiceRequestSetStatus} from './ServiceRequestSetStatus';
import {TypeAccountRoles} from '../../../shared/types/user-account.types';
import {get} from 'lodash';
import {checkAuthorities} from '../../../shared/helpers/checkAuthorities';
import {ArchiveButton} from '../../ArchiveButton';
import {getIsUserCustomer, getIsUserWorker} from '../../../global/lib';
import {useRefreshCompanyTariff} from '../../../modules/company-tariff';

const CANCEL_REQUEST_ALLOWED_ROLES = [
  TypeAccountRoles.MANAGER,
  TypeAccountRoles.PROJECT_MANAGER,
  TypeAccountRoles.DIRECTOR,
  TypeAccountRoles.FUNCTIONAL_ADMIN,
  TypeAccountRoles.ADMIN,
];

export const ServiceRequestFooter: FC = memo(() => {
  const {user} = useRecoilValue(authState);
  const serviceRequest = useRecoilValue(currentServiceRequestState);
  const [{state, contents: reasonRejections}, setReasonRejections] = useRecoilStateLoadable(reasonRejectionsDictQuery);
  const [activeTab, setActiveTab] = useRecoilState(serviceRequestActiveTabState);
  const [{form, isDirty}, setEditableWorkData] = useRecoilState(editableWorkData);
  const delayData = useRecoilValue(currentReasonDelay);
  const baseForm = useRecoilValue(baseWorkData);
  const triggerQuery = useSetRecoilState(serviceRequestsQueryTrigger);
  const triggerFilesQuery = useSetRecoilState(contentTreeFilesQueryTrigger);
  const {bus} = useContext(EventBusContext);
  const [clickedMenuBtn, setClickedMenuBtn] = useState<string | null>(null);
  const [docsToReplace, setDocsToReplace] = useRecoilState(docsToReplaceState);
  const refreshCompanyTariff = useRefreshCompanyTariff();

  const isCustomer = getIsUserCustomer(user);
  const isManager = user?.roles?.includes(TypeAccountRoles.MANAGER);
  const isCancelButtonVisible = checkAuthorities(CANCEL_REQUEST_ALLOWED_ROLES, get(user, 'roles', []));

  const [loading, setLoading] = useState(false);

  const [openReject, setOpenReject] = useState(false);
  const [openSetStatus, setOpenSetStatus] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDone, setOpenDone] = useState(false);
  const [openRevision, setOpenRevision] = useState(false);

  const populatedForm = form ? populateBaseForm(form, baseForm) : null;
  const canRestored =
    serviceRequest?.statusCode === StatusCodeEnum.CANCELLED || serviceRequest?.statusCode === StatusCodeEnum.REJECTED;
  const canRevision =
    serviceRequest?.statusCode === StatusCodeEnum.EXECUTED || serviceRequest?.statusCode === StatusCodeEnum.DONE;

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setClickedMenuBtn(event.currentTarget.id);
  }, []);

  const allMandatoryExist = populatedForm
    ? Object.entries(populatedForm)
        .filter((i) => i[1].mandatory)
        .every((i) => (Array.isArray(i[1].value) && i[1].value.length) || (!Array.isArray(i[1].value) && i[1].value))
    : true;

  const turnOffEditMode = () => {
    triggerQuery((v) => v + 1);
    triggerFilesQuery((v) => v + 1);
    setActiveTab({activeTab: 'deviceInfo'});
    setOpenEdit(false);
    setDocsToReplace([]);
  };

  const toRevision = (setLoading: (arg: boolean) => void) => {
    setLoading(true);
    ServiceRequestService.setStatus(serviceRequest?.id as number, StatusCodeEnum.READY_TO_PROCESS)
      .then(() => {
        triggerQuery((v) => v + 1);
        toast.success(`Заявка №${serviceRequest?.id} возвращена ${canRevision ? 'на доработку' : 'в работу'}`, {
          containerId: 'default',
          autoClose: 2000,
          hideProgressBar: true,
          pauseOnHover: false,
          draggable: false,
        });
        setOpenRevision(false);
      })
      .catch((err) => {
        console.error(err);
        bus?.emit('error', [err]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const finishServiceRequest = async (setLoading: (arg: boolean) => void) => {
    setLoading(true);
    try {
      await ServiceRequestService.setStatus(serviceRequest?.id as number, StatusCodeEnum.DONE);
      triggerQuery((v) => v + 1);
      toast.success(`Заявка №${serviceRequest?.id} завершена`, {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });
      setLoading(false);
      setOpenDone(false);
    } catch (err) {
      setLoading(false);
      bus?.emit('error', [err]);
    }
  };

  const saveEditHandler = async (setLoading: (arg: boolean) => void) => {
    try {
      if (!(isDirty || docsToReplace.length) && !form && !serviceRequest) return;

      if (!populatedForm) return;

      setLoading(true);

      await Promise.all(
        filterDivider(Object.entries(populatedForm), (item) => item[1].type === WorkContentType.DOCUMENTS)
          .reduce<Promise<unknown>[]>((prev, curr) => {
            return curr[0][1].type === WorkContentType.DOCUMENTS
              ? [
                  ...prev,
                  ...curr.reduce<Promise<unknown>[]>((prevDocArr, currDoc) => {
                    if (typeof currDoc[1].value !== 'string' && currDoc[1].value.length) {
                      return [
                        ...prevDocArr,
                        ServiceRequestService.setDocuments((serviceRequest as IServiceRequest).id as number, {
                          documents: currDoc[1].value,
                          workContentId: currDoc[1].id,
                          isReplaceFileNameRequired: currDoc[1].inputType !== InputTypeEnum.FILE_PICKER,
                        })
                          .then(() => {
                            const prefix = getModelNamePrefix(currDoc[1].inputType);

                            const newForm = form ? {...form, [`${prefix}${currDoc[1].id}`]: ''} : form;

                            setEditableWorkData((prev) => ({
                              ...prev,
                              form: newForm ? newForm : prev.form,
                            }));
                          })
                          .finally(refreshCompanyTariff),
                      ];
                    }

                    return prevDocArr;
                  }, []),
                ]
              : [
                  ...prev,
                  ServiceRequestService.setWorksData(
                    (serviceRequest as IServiceRequest).id as number,
                    curr as [
                      string,
                      {
                        id: number;
                        value: string;
                        type: string;
                        mandatory: boolean;
                      },
                    ][],
                  ),
                ];
          }, [])
          .concat(
            docsToReplace?.length
              ? docsToReplace.map((i) =>
                  DocumentService.replaceDocument(i.docId, Number(serviceRequest?.id), i.file).finally(
                    refreshCompanyTariff,
                  ),
                )
              : [Promise.resolve()],
          ),
      );

      turnOffEditMode();
      toast.success('Изменения сохранены', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });
    } catch (err) {
      turnOffEditMode();
      bus?.emit('error', [err]);
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    if (activeTab) {
      setActiveTab({...activeTab, editMode: false});
      setDocsToReplace([]);
    }
  };

  const canEdit = (user as IUserInfo).roles?.some((i) =>
    [TypeAccountRoles.ADMIN, TypeAccountRoles.MANAGER].includes(i),
  );

  const isWorker = getIsUserWorker(user);

  const canEditAsWorker = isWorker && serviceRequest?.statusCode === StatusCodeEnum.IN_PROGRESS;

  const showEditBtn = (canEdit || canEditAsWorker) && activeTab?.activeTab === 'deviceInfo' && !activeTab?.editMode;

  const isDoneBtnDisabled = [
    activeTab?.editMode,
    !allMandatoryExist,
    !form,
    serviceRequest?.statusCode !== StatusCodeEnum.EXECUTED,
  ].some(Boolean);

  useEffect(() => {
    if (state === 'hasValue') setReasonRejections(reasonRejections);
    if (state === 'hasError') bus?.emit('error', [reasonRejections, {toastId: 'reasonRejections'}]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      <Stack sx={{mt: 4}} direction="row" justifyContent="flex-end" spacing={2}>
        {/* TODO: отключает печатные формы до момента, когда задача будет проработана */}
        {/* {!isCustomer &&
          serviceRequest &&
          (serviceRequest.statusCode === 'EXECUTED' || serviceRequest.statusCode === 'DONE') && (
            <LoadingButton
              id="adpu-btn"
              loading={loading && clickedMenuBtn === 'adpu-btn'}
              onClick={(e) => {
                handleClick(e);
                setLoading(true);
                generatePDF(() => getTargetElement('adpu-doc'), {
                  canvas: {
                    mimeType: 'image/jpeg',
                    qualityRatio: 0.1,
                  },
                }).finally(() => {
                  setLoading(false);
                });
              }}
              size="small"
              variant="outlined"
              startIcon={<AdfScannerIcon />}
            >
              Печать АДПУ
            </LoadingButton>
          )} */}
        {serviceRequest &&
          (serviceRequest.statusCode === StatusCodeEnum.EXECUTED ||
            serviceRequest.statusCode === StatusCodeEnum.DONE) && (
            <LoadingButton
              id="images-btn"
              loading={loading && clickedMenuBtn === 'images-btn'}
              onClick={(e) => {
                handleClick(e);
                setLoading(true);
                ServiceRequestService.getImages(serviceRequest.id)
                  .then((res) => {
                    resolveAndDownloadBlob(res, `images_заявка_${serviceRequest.id}.zip`);
                  })
                  .catch((err) => bus?.emit('error', [err]))
                  .finally(() => {
                    setLoading(false);
                  });
              }}
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              Скачать фотографии
            </LoadingButton>
          )}
        {isManager && serviceRequest && (canRestored || canRevision) && (
          <>
            <LoadingButton
              id="adpu-btn"
              loading={loading && clickedMenuBtn === 'adpu-btn'}
              onClick={() => setOpenRevision(true)}
              size="small"
              variant="outlined"
              color="warning"
            >
              {`Вернуть ${canRevision ? 'на доработку' : 'в работу'}`}
            </LoadingButton>
            {openRevision && (
              <ModalContainer
                sideEffect={toRevision}
                renderModal={(sideEffect, loading) => (
                  <ConfirmAction
                    loading={loading}
                    open={openRevision}
                    handleClose={() => setOpenRevision(false)}
                    handleSideEffect={sideEffect}
                    title={`Вернуть заявку ${canRevision ? 'на доработку' : 'в работу'}?`}
                    buttonTitle="Вернуть"
                  />
                )}
              />
            )}
          </>
        )}
        {!isCustomer &&
        serviceRequest &&
        (serviceRequest.statusCode === StatusCodeEnum.EXECUTED ||
          serviceRequest.statusCode === StatusCodeEnum.IN_PROGRESS) ? (
          <>
            {!activeTab?.editMode && isCancelButtonVisible ? (
              <>
                {openReject && state === 'hasValue' ? (
                  <ServiceRequestRejectModal
                    id={serviceRequest.id}
                    reasonRejections={reasonRejections?.content}
                    open={openReject}
                    handleClose={() => setOpenReject(false)}
                  />
                ) : null}
                <LoadingButton
                  size="small"
                  loading={state === 'loading'}
                  disabled={
                    !Array.isArray(reasonRejections?.content) ||
                    (Array.isArray(reasonRejections?.content) && !reasonRejections?.content?.length)
                  }
                  onClick={() => setOpenReject(true)}
                  color="error"
                  variant="outlined"
                >
                  Отменить заявку
                </LoadingButton>
              </>
            ) : null}
            {activeTab?.activeTab === 'deviceInfo' && activeTab?.editMode ? (
              <>
                <Button size="small" onClick={cancelEdit}>
                  Отменить
                </Button>
                <Tooltip title="Сохранить изменения в рабочих данных">
                  <Button
                    variant="outlined"
                    onClick={() => setOpenEdit(true)}
                    disabled={
                      !(isDirty || docsToReplace.length) ||
                      (!allMandatoryExist && serviceRequest.statusCode !== StatusCodeEnum.IN_PROGRESS)
                    }
                  >
                    Сохранить
                  </Button>
                </Tooltip>
              </>
            ) : null}
            {showEditBtn ? (
              <Button
                size="small"
                variant="outlined"
                endIcon={<EditIcon />}
                onClick={() => setActiveTab({activeTab: 'deviceInfo', editMode: true, onlyUpload: isWorker})}
              >
                Редактировать
              </Button>
            ) : null}
            <Button size="small" disabled={isDoneBtnDisabled} onClick={() => setOpenDone(true)} variant="contained">
              Завершить
            </Button>
            {openEdit && (
              <ModalContainer
                sideEffect={saveEditHandler}
                renderModal={(sideEffect, loading) => (
                  <ConfirmAction
                    loading={loading}
                    open={openEdit}
                    handleClose={() => setOpenEdit(false)}
                    handleSideEffect={sideEffect}
                    title={`Вы уверены, что хотите внести изменения?${isWorker ? '' : ' При загрузке новых фото вместо старых, старые будут удалены'}`}
                    buttonTitle="Сохранить"
                  />
                )}
              />
            )}
            {openDone && (
              <ModalContainer
                sideEffect={finishServiceRequest}
                renderModal={(sideEffect, loading) => (
                  <ConfirmAction
                    loading={loading}
                    open={openDone}
                    handleClose={() => setOpenDone(false)}
                    handleSideEffect={sideEffect}
                    title="Завершить заявку?"
                    buttonTitle="Завершить"
                  />
                )}
              />
            )}
          </>
        ) : isCancelButtonVisible &&
          serviceRequest &&
          serviceRequest.statusCode !== StatusCodeEnum.DONE &&
          serviceRequest.statusCode !== StatusCodeEnum.CANCELLED &&
          serviceRequest.statusCode !== StatusCodeEnum.REJECTED &&
          serviceRequest.statusCode !== StatusCodeEnum.APPROVED ? (
          <>
            <LoadingButton
              loading={state === 'loading'}
              disabled={
                !Array.isArray(reasonRejections?.content) ||
                (Array.isArray(reasonRejections?.content) && !reasonRejections?.content?.length)
              }
              onClick={() => setOpenReject(true)}
              size="small"
              color="error"
              variant="outlined"
            >
              Отменить заявку
            </LoadingButton>
            {openReject && state === 'hasValue' ? (
              <ServiceRequestRejectModal
                id={serviceRequest.id}
                reasonRejections={reasonRejections?.content}
                open={openReject}
                handleClose={() => setOpenReject(false)}
              />
            ) : null}
          </>
        ) : null}
        {!isCustomer && serviceRequest && serviceRequest.statusCode === StatusCodeEnum.DONE ? (
          <>
            <Button onClick={() => setOpenSetStatus(true)} size="small" color="warning" variant="contained">
              Открыть заявку
            </Button>
            {openSetStatus ? (
              <ServiceRequestSetStatus
                id={serviceRequest.id}
                open={openSetStatus}
                handleClose={() => setOpenSetStatus(false)}
              />
            ) : null}
          </>
        ) : null}
      </Stack>

      {!isCustomer && serviceRequest && delayData.delay && serviceRequest.statusCode !== StatusCodeEnum.CREATED && (
        <div style={{position: 'fixed', zIndex: -9999}}>
          <DelayDocTemplate
            data={
              normalizeServiceRequestForDelayDoc(
                serviceRequest as IServiceRequest,
                user as any,
                delayData as any,
              ) as IDelayDocVars
            }
          />
        </div>
      )}
      {!isCustomer &&
        serviceRequest &&
        (serviceRequest.statusCode === StatusCodeEnum.EXECUTED ||
          serviceRequest.statusCode === StatusCodeEnum.DONE) && (
          <div style={{position: 'fixed', zIndex: -9999}}>
            <ADPUDocTemplate data={normalizeServiceRequestForADPUDoc(serviceRequest as IServiceRequest, user as any)} />
          </div>
        )}
    </>
  );
});
